import { Octokit } from '@octokit/rest';

const octokit = new Octokit({
  auth: import.meta.env.VITE_GITHUB_TOKEN,
  baseUrl: 'https://api.github.com'
});

const owner = import.meta.env.VITE_GITHUB_OWNER;
const repo = import.meta.env.VITE_GITHUB_REPO;

// Log configuration for testing
console.log('GitHub Configuration:', {
  owner,
  repo,
  hasToken: !!import.meta.env.VITE_GITHUB_TOKEN
});

/**
 * Ensures the image upload issue exists
 */
const ensureImageUploadIssue = async () => {
  try {
    // Search for the image upload issue
    const { data: issues } = await octokit.issues.listForRepo({
      owner,
      repo,
      labels: 'image-uploads',
      state: 'open'
    });

    if (issues.length > 0) {
      return issues[0].number;
    }

    // Create the issue if it doesn't exist
    const { data: issue } = await octokit.issues.create({
      owner,
      repo,
      title: 'Feedback Image Uploads',
      body: 'This issue is used to store feedback image uploads. Please do not close.',
      labels: ['image-uploads']
    });

    return issue.number;
  } catch (error) {
    console.error('Error ensuring image upload issue:', error);
    throw error;
  }
};

/**
 * Uploads an image directly to GitHub issue
 */
const uploadImage = async (file) => {
  const issueNumber = await ensureImageUploadIssue();
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        // Create a comment with the image
        const { data } = await octokit.issues.createComment({
          owner,
          repo,
          issue_number: issueNumber,
          body: `![${file.name}](${reader.result})`
        });
        
        // Extract image URL from the comment
        const imageUrl = data.body.match(/!\[.*?\]\((.*?)\)/)[1];
        resolve(imageUrl);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Creates a GitHub issue with the feedback and files
 */
export const createFeedbackIssue = async (feedback, files) => {
  try {
    // Format current date
    const date = new Date();
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    // Create issue title
    const title = `Website Feedback: ${formattedDate} at ${formattedTime}`;

    // Create issue body with feedback and file summary
    const issueBody = `
## Feedback Details
${feedback}

${files.length > 0 ? `
## File Attachments
${files.map((file, index) => `${index + 1}. \`${file.name}\` (${formatFileSize(file.size)})`).join('\n')}

_Total attachments: ${files.length}_
` : ''}

---
_Submitted on ${formattedDate} at ${formattedTime}_
    `.trim();

    // Create the issue
    const { data: issue } = await octokit.issues.create({
      owner,
      repo,
      title,
      body: issueBody,
      labels: ['feedback', 'needs-review']
    });

    // Upload files as separate comments
    if (files.length > 0) {
      for (const file of files) {
        const fileContent = await getFileContent(file);
        const comment = file.type.startsWith('image/') 
          ? `### File: ${file.name}\n_Size: ${formatFileSize(file.size)}_\n\n![${file.name}](${fileContent})`
          : `### File: ${file.name}\n_Size: ${formatFileSize(file.size)}_\n\n\`\`\`\n${fileContent}\n\`\`\``;
        
        await octokit.issues.createComment({
          owner,
          repo,
          issue_number: issue.number,
          body: comment
        });
      }
    }

    return issue;
  } catch (error) {
    console.error('Error creating GitHub issue:', error);
    throw error;
  }
};

/**
 * Gets the content of a file as a data URL or text
 */
const getFileContent = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    
    if (file.type.startsWith('image/')) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsText(file);
    }
  });
};

/**
 * Format file size in a human-readable format
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
}; 